import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchData, handleAddItem, handleGetDataByPost, handleUpdateItem } from '../../utils/data'
import { Booking, Room } from '../../types'
import i18n from '../../i18n';

const lang = i18n.language === "ar" ? "ar" : "en";

interface BookingState {
  bookedStays: Booking[];
  bookedHalls: Booking[];
  booking: Booking | null;
  checkIn: Date | null;
  checkOut: Date | null;
  hotelId: number | null;
  roomIds: number[];
  selectedRooms: Room[];
  duration: 1 | 2;
  paymentRes: any;
  selectedOptionalService: { id: number | string, price: number | string }[];
  loading: boolean;
}

// ----------- Async Thunks -------------
export const addBookStay = createAsyncThunk("post/BookStay", async (data: any) => {
  const res = await handleAddItem(data, `BookPlace/bookStay`, "POST");
  return res;
});

export const addBookHall = createAsyncThunk("post/BookHall", async (data: any) => {
  const res = await handleAddItem(data, `BookHall/bookHall`, "POST");
  return res;
});

export const addReview = createAsyncThunk("post/Review", async (data: any) => {
  await handleAddItem(data, `Review/addReview`, "POST");
});

export const addPayment = createAsyncThunk("post/payment", async ({ userId, bookId, type }: { userId: string | number | undefined, bookId: number | undefined | null, type: "Hall" | "Stay" }) => {
  const res = await handleGetDataByPost(userId, `Payment/generatePaymentLink?bookId=${bookId}&userId=${userId}&type=${type}`, "POST");
  return res;
});

export const fetchBookStays = createAsyncThunk("get/BookStays", async (id: string | number) => {
  const data = await fetchData(`BookPlace/getBookStays?UserID=${id}&lang=${lang}`, true);
  return data;
});

export const cancelBook = createAsyncThunk("PUT/prereturnBook", async ({ userId, bookId }: any) => {
  await handleUpdateItem("", `BookPlace/preReturnBook?userId=${userId}&bookId=${bookId}`, "PUT");
});
export const cancelHallBook = createAsyncThunk("PUT/prereturnBook", async ({ userId, bookId }: any) => {
  await handleUpdateItem("", `BookHall/preReturnBook?userId=${userId}&bookId=${bookId}`, "PUT");
});
export const refundBook = createAsyncThunk("PUT/returnBook", async ({ userId, bookId }: any) => {
  await handleUpdateItem("", `BookPlace/returnBook?userId=${userId}&bookId=${bookId}`, "PUT");
});
export const refundHallBook = createAsyncThunk("PUT/returnBook", async ({ userId, bookId }: any) => {
  await handleUpdateItem("", `BookHall/returnBook?userId=${userId}&bookId=${bookId}`, "PUT");
});

export const acceptHallBook = createAsyncThunk("PUT/WaitingBook", async (bookId: number) => {
  await handleUpdateItem("", `BookHall/changeWaitingBook?isPending=true&bookId=${bookId}`, "PUT");
});
export const acceptBook = createAsyncThunk("PUT/WaitingBook", async (bookId: number) => {
  await handleUpdateItem("", `BookPlace/changeWaitingBook?isPending=true&bookId=${bookId}`, "PUT");
});

export const refuseBook = createAsyncThunk("PUT/WaitingBook", async (bookId: number) => {
  await handleUpdateItem("", `BookPlace/changeWaitingBook?isPending=false&bookId=${bookId}`, "PUT");
});
export const refuseHallBook = createAsyncThunk("PUT/WaitingBook", async (bookId: number) => {
  await handleUpdateItem("", `BookHall/changeWaitingBook?isPending=false&bookId=${bookId}`, "PUT");
});

export const changePaymentMethodInBook = createAsyncThunk(
  "PUT/changePaymentMethod",
  async ({ bookId, userId, paymentMethod }: { bookId: number, userId: string | number | undefined, paymentMethod: string }) => {
    await handleUpdateItem("", `BookPlace/changePaymentMethodInBook?userId=${userId}&paymentMethod=${paymentMethod}&bookId=${bookId}`, "PUT");
  }
);
export const changePaymentMethodInHallBook = createAsyncThunk(
  "PUT/changePaymentMethod",
  async ({ bookId, userId, paymentMethod }: { bookId: number, userId: string | number | undefined, paymentMethod: string }) => {
    await handleUpdateItem("", `BookHall/changePaymentMethodInBook?userId=${userId}&paymentMethod=${paymentMethod}&bookId=${bookId}`, "PUT");
  }
);

export const fetchBookHalls = createAsyncThunk("get/BookHalls", async (id: string | number) => {
  const data = await fetchData(`BookHall/getBookHalls?UserID=${id}`, true);
  return data;
});

export const fetchBookById = createAsyncThunk("get/BookById", async (bookId: number) => {
  const data = await fetchData(`BookPlace/getBookById?bookId=${bookId}&lang=${lang}`, true);
  return data;
});
export const fetchHallBookById = createAsyncThunk("get/HallBookById", async (bookId: number) => {
  const data = await fetchData(`BookHall/getBookById?bookId=${bookId}&lang=${lang}`, true);
  return data;
});

// ----------- Initial State -------------
const initialState: BookingState = {
  bookedStays: [],
  bookedHalls: [],
  booking: null,
  duration: 1,
  checkIn: sessionStorage.getItem('checkIn')
    ? new Date(sessionStorage.getItem('checkIn') as string)
    : null,
  checkOut: sessionStorage.getItem('checkOut')
    ? new Date(sessionStorage.getItem('checkOut') as string)
    : null,
  hotelId: Number(sessionStorage.getItem('hotelId')) || null,
  roomIds: JSON.parse(sessionStorage.getItem('roomIds') || '[]'),
  selectedRooms: JSON.parse(sessionStorage.getItem('selectedRooms') || '[]'),
  selectedOptionalService: JSON.parse(sessionStorage.getItem("optionalServiceIds") || "[]"),
  paymentRes: null,
  loading: false,
}

// ----------- Slice -------------
const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setDates: (
      state,
      action: PayloadAction<{ checkIn: Date | null; checkOut?: Date | null; duration?: 1 | 2 }>,
    ) => {
      state.checkIn = action.payload.checkIn;
      state.checkOut = action.payload.checkOut ?? state.checkOut;
      state.duration = action.payload.duration ?? state.duration

      if (action.payload.checkIn) {
        sessionStorage.setItem('checkIn', action.payload.checkIn.toISOString());
      } else {
        sessionStorage.removeItem('checkIn');
      }

      if (action.payload.checkOut) {
        sessionStorage.setItem('checkOut', action.payload.checkOut.toISOString());
      } else {
        sessionStorage.removeItem('checkOut');
      }
      if (action.payload.duration) {
        sessionStorage.setItem('duration', action.payload.duration.toString());
      } else {
        sessionStorage.removeItem('duration');
      }
    },
    setHotel: (state, action: PayloadAction<number>) => {
      state.hotelId = action.payload
      sessionStorage.setItem('hotelId', String(action.payload))
    },
    setRooms: (state, action: PayloadAction<number[]>) => {
      state.roomIds = action.payload;
      sessionStorage.setItem('roomIds', JSON.stringify(action.payload));
    },
    setSelectedRooms: (state, action: PayloadAction<Room[]>) => {
      state.selectedRooms = action.payload;
      sessionStorage.setItem('selectedRooms', JSON.stringify(action.payload));
    },
    setOptionalServices: (state, action) => {
      state.selectedOptionalService = action.payload;
      sessionStorage.setItem("optionalServiceIds", JSON.stringify(action.payload));
    },
    clearBooking: (state) => {
      state.checkIn = null
      state.checkOut = null
      state.hotelId = null
      state.duration = 1
      state.selectedOptionalService = [];
      state.roomIds = [];
      sessionStorage.removeItem('roomIds');
      state.selectedRooms = [];
      sessionStorage.removeItem('selectedRooms');

      sessionStorage.removeItem('checkIn')
      sessionStorage.removeItem('checkOut')
      sessionStorage.removeItem('hotelId')
      sessionStorage.removeItem('duration')
      sessionStorage.removeItem('guests')
      sessionStorage.removeItem("optionalServiceIds");
    },
    clearSavedRooms:(state)=>{
            state.selectedOptionalService = [];
      state.roomIds = [];
      sessionStorage.removeItem('roomIds');
      state.selectedRooms = [];
      sessionStorage.removeItem('selectedRooms');
    }
  },
  extraReducers: (builder) => {
    builder
      // Add Payment
      .addCase(addPayment.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentRes = action.payload;
      })

      // Fetch Stays
      .addCase(fetchBookStays.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBookStays.fulfilled, (state, action) => {
        state.loading = false;
        state.bookedStays = action.payload || [];
      })

      // Fetch Halls
      .addCase(fetchBookHalls.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBookHalls.fulfilled, (state, action) => {
        state.loading = false;
        state.bookedHalls = action.payload || [];
      })

      // Fetch by ID
      .addCase(fetchBookById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBookById.fulfilled, (state, action) => {
        state.loading = false;
        state.booking = action.payload || null;
      })
      // Fetch by ID
      .addCase(fetchHallBookById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHallBookById.fulfilled, (state, action) => {
        state.loading = false;
        state.booking = action.payload || null;
      })

      .addCase(addBookHall.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBookHall.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addBookStay.pending, (state) => {
        state.loading = true;
      })
      .addCase(addBookStay.fulfilled, (state, action) => {
        state.loading = false;
      })
  }
})

export const {
  setDates,
  setHotel,
  setRooms,
  clearBooking,
  setOptionalServices,
  setSelectedRooms,
  clearSavedRooms
} = bookingSlice.actions

export default bookingSlice.reducer
